import { useMemo } from 'react';

import { ENV } from 'services/environment/environment';
import { useIntegration } from 'services/repositories/integrations/integrations';
import { useRelationship, useSenderProcessSpecification } from 'services/repositories/relationships/relationships';
import { useOCRStore } from 'stores/ocr/ocr';
import {
  DocumentType,
  DocumentTypeWithVirtual,
  MessageV2DTO,
  PartnerDTO,
  PartnerV2,
  ProcessSpecificationDTO,
} from 'support/types';
import { uploadLoader } from 'components/Form/FileInput/Loaders/uploadLoader/uploadLoader';

import { GenericError } from '../errors/errors';
import { useDocumentCreationRelationship } from '../relationships/relationships';

const DOCUMENTS_WITH_OCR = [DocumentType.shippingNotice, DocumentType.invoice];

export const useShouldShowOcr = (documentType: DocumentType | undefined): boolean => {
  const { overrideEnabled } = useOCRStore();

  return useMemo(() => {
    if (!documentType || !DOCUMENTS_WITH_OCR.includes(documentType)) {
      return false;
    }

    if (overrideEnabled) {
      return true;
    }

    return DOCUMENTS_WITH_OCR.includes(documentType);
  }, [documentType, overrideEnabled]);
};

const isBetaOCR = (senderProcessSpecification: ProcessSpecificationDTO | undefined) => {
  if (ENV.BACKEND_ENV === 'staging') {
    return false;
  }
  return !senderProcessSpecification?.meta?.ocr?.enabled;
};

export const useIsBetaOCR = ({
  targetRelationshipId,
  sourceMessageId,
}: {
  targetRelationshipId: string | undefined;
  sourceMessageId: string | undefined;
}) => {
  const { overrideEnabled } = useOCRStore();
  const { data: senderProcessSpecification, isLoading } = useSenderProcessSpecification({
    variables: { relationshipId: targetRelationshipId, sourceMessageId },
  });

  return { isLoading, isBeta: overrideEnabled ? false : isBetaOCR(senderProcessSpecification) };
};

type UseOCRAvailabilityProps = {
  fromDocument: Pick<MessageV2DTO, 'id'>;
  toDocumentType: DocumentType;
};

export const useOCRAvailability = ({ fromDocument, toDocumentType }: UseOCRAvailabilityProps) => {
  const relationship = useDocumentCreationRelationship({
    sourceMessageId: fromDocument.id,
    documentType: toDocumentType,
  });

  const { isLoading, isBeta } = useIsBetaOCR({
    targetRelationshipId: relationship?.id,
    sourceMessageId: fromDocument.id,
  });

  const shouldShowOCR = useShouldShowOcr(toDocumentType);

  return { isBeta: isBeta, shouldShowOCR, isLoading };
};

export const ocrUploadFileLoader = ({
  documentType,
  currentPartnerId,
  sourceDocumentId,
}: {
  documentType: DocumentType | undefined | null;
  currentPartnerId: string | undefined;
  sourceDocumentId: string | undefined;
}) => {
  return uploadLoader({
    url: `${ENV.DOCUPARSE_API_URL}api/v1/upload`,
    bearerToken: ENV.DOCUPARSE_API_KEY,
    body: {
      sender_partner_id: currentPartnerId,
      document_type: documentType,
      source_document_id: sourceDocumentId,
    },
  });
};

type OCRExtractFromSpecLoaderArgs = {
  hash: string;
  documentType: DocumentType;
  virtualDocumentType: DocumentTypeWithVirtual;
  initialData: Record<string, any>;
  senderSpec: Pick<ProcessSpecificationDTO, 'fields'>;
  receiverSpec: Pick<ProcessSpecificationDTO, 'fields'>;
  senderPartner: Pick<PartnerV2, 'id' | 'name'>;
  receiverPartner: Pick<PartnerV2, 'id' | 'name'>;
  sessionId: string | undefined;
  isAdmin: boolean;
};
export const extractDataFromSpec = async ({
  hash,
  documentType,
  initialData,
  senderPartner,
  senderSpec,
  receiverPartner,
  receiverSpec,
  sessionId,
  isAdmin,
  virtualDocumentType,
}: OCRExtractFromSpecLoaderArgs): Promise<DocuparseResponseSchema['/v1/extract-from-spec']> => {
  return fetch(`${ENV.DOCUPARSE_API_URL}api/v1/extract-from-spec`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${ENV.DOCUPARSE_API_KEY}`,
    },
    body: JSON.stringify({
      document_hash: hash,
      document_type: documentType,
      initial_data: initialData,
      is_internal_test: isAdmin,
      receiver: { spec: receiverSpec },
      sender: { spec: senderSpec },
      context: {
        source_partner: senderPartner,
        destination_partner: receiverPartner,
        virtual_document_type: virtualDocumentType,
        document_type: documentType,
        session_id: sessionId,
        environment: ENV.BACKEND_ENV,
      },
    }),
  }).then(async (res) => {
    if (!res.ok) {
      const clone = res.clone();
      const body = await clone.text();
      throw new GenericError('Failed to extract document', body);
    }
    return res.json();
  });
};

export type DocuparseResponseSchema = {
  '/v1/upload': {
    success: boolean;
    data: {
      hash: string;
      name: string;
      size: number;
      sizeMB: string;
      type: string; // mime type of the file: application/pdf, image/jpeg, etc.
    };
  };
  '/v1/extract-from-spec': {
    success: boolean;
    matcher: string;
    schema: {
      aliasMapping: Record<string, string>;
      schema: Record<string, any>;
    };
    aliasMapping: Record<string, any>;
    extracted: Record<string, any>;
    merged: Record<string, any>;
    initial_data?: Record<string, any>;
  };
};

export const useUnattendedOcrEmail = ({
  relationshipId,
  currentPartner,
}: {
  relationshipId: string | undefined;
  currentPartner: PartnerDTO | undefined;
}) => {
  const { data: relationship } = useRelationship({
    variables: { relationshipId: relationshipId },
  });

  const userIsSender = relationship?.sender_partner.id === currentPartner?.id;
  const { data: integration } = useIntegration({
    variables: {
      withConfiguration: true,
      id: userIsSender
        ? relationship?.sender_integration?.production?.id
        : relationship?.receiver_integration?.production?.id,
    },
  });

  return integration?.connectorConfig?.unattendedOcrEmail;
};
